import type CargaItem from "../types/carga_item";
import { getOrdemEntrega } from "../types/carga_item";

export interface Entrega {
  chave: string;
  j14_ukey: string;
  j10_ukey_rota: string;
  notas: CargaItem[];
  fantasias: string;
  razao_social: string;
  cnpj: string;
  endereco: string;
  numero: string;
  bairro: string;
  cep: string;
  cidade: string;
  uf: string;
  celulares: string[];
  latitude: string;
  longitude: string;
  pedidos: string[];
  tipos_pagamento: string[];
  notas_numeros: string[];
  ordem_entrega_min: number;
  tem_reentrega: boolean;
  tem_nota_pallet: boolean;
  link_app: string;
}

const digitos = (valor?: string) => String(valor || "").replace(/\D/g, "");
const normaliza = (valor?: string) => String(valor || "").trim().replace(/\s+/g, " ").toLowerCase();

const distintos = (valores: (string | undefined)[]): string[] => {
  const vistos = new Set<string>();
  const resultado: string[] = [];
  for (const valor of valores) {
    const texto = String(valor || "").trim();
    if (!texto || vistos.has(texto)) continue;
    vistos.add(texto);
    resultado.push(texto);
  }
  return resultado;
};

const comparaNotaNumero = (a: CargaItem, b: CargaItem) =>
  (a.nota_numero || "").localeCompare(b.nota_numero || "");

export function chaveDaEntrega(item: CargaItem): string {
  const cliente = String(item.cliente_ukey || "").trim();
  if (cliente) {
    return `${item.j14_ukey}|c:${cliente}`;
  }
  return [
    `${item.j14_ukey}|f:${digitos(item.cliente_cnpj)}`,
    normaliza(item.cliente_endereco),
    normaliza(item.cliente_numero),
    digitos(item.cliente_cep),
  ].join("|");
}

function montaEntrega(chave: string, notasDoGrupo: CargaItem[]): Entrega {
  const notas = [...notasDoGrupo].sort(comparaNotaNumero);
  const primeira = notas[0];
  const comCoordenada = notas.find((n) => !!n.cliente_latitude && !!n.cliente_longitude);
  const ordens = notas.map(getOrdemEntrega).filter((o) => o > 0);
  const comLink = notas.find((n) => !!n.nota_dados_complementares_entrega_link_app);

  return {
    chave,
    j14_ukey: primeira.j14_ukey,
    j10_ukey_rota: primeira.j10_ukey,
    notas,
    fantasias: distintos(notas.map((n) => n.cliente_fantasia)).join(" / "),
    razao_social: primeira.cliente_razao_social,
    cnpj: primeira.cliente_cnpj,
    endereco: primeira.cliente_endereco,
    numero: primeira.cliente_numero,
    bairro: primeira.cliente_bairro,
    cep: primeira.cliente_cep,
    cidade: primeira.cliente_cidade,
    uf: primeira.cliente_uf,
    celulares: distintos(notas.map((n) => n.cliente_celular)),
    latitude: comCoordenada?.cliente_latitude || "",
    longitude: comCoordenada?.cliente_longitude || "",
    pedidos: distintos(notas.map((n) => n.pedido_numero)),
    tipos_pagamento: distintos(notas.map((n) => n.pedido_tipo_documento_nome)),
    notas_numeros: notas.map((n) => n.nota_numero),
    ordem_entrega_min: ordens.length ? Math.min(...ordens) : 0,
    tem_reentrega: notas.some((n) => n.e_reentrega == "1"),
    tem_nota_pallet: notas.some((n) => n.e_nota_pallet == "1"),
    link_app: comLink?.nota_dados_complementares_entrega_link_app || "",
  };
}

export function agruparItensPorEntrega(itens: CargaItem[]): Entrega[] {
  const grupos = new Map<string, CargaItem[]>();
  for (const item of itens || []) {
    const chave = chaveDaEntrega(item);
    const grupo = grupos.get(chave);
    if (grupo) {
      grupo.push(item);
    } else {
      grupos.set(chave, [item]);
    }
  }
  const entregas: Entrega[] = [];
  grupos.forEach((notas, chave) => entregas.push(montaEntrega(chave, notas)));
  return entregas;
}

export function filtrarEntregas(entregas: Entrega[], filtro: string): Entrega[] {
  const termo = String(filtro || "").trim().toLowerCase();
  if (!termo) return entregas;
  return entregas.filter((entrega) =>
    entrega.notas.some((n) =>
      (n.cliente_fantasia || "").toLowerCase().includes(termo) ||
      (n.cliente_razao_social || "").toLowerCase().includes(termo) ||
      (n.nota_numero || "").toLowerCase().includes(termo)
    )
  );
}

export function ordenarEntregas(entregas: Entrega[], por: "entrega" | "nota"): Entrega[] {
  const copia = [...entregas];
  const primeiraNota = (e: Entrega) => e.notas_numeros[0] || "";
  if (por === "entrega") {
    copia.sort((a, b) => {
      if (a.ordem_entrega_min !== b.ordem_entrega_min) return a.ordem_entrega_min - b.ordem_entrega_min;
      return primeiraNota(a).localeCompare(primeiraNota(b));
    });
    return copia;
  }
  copia.sort((a, b) => primeiraNota(a).localeCompare(primeiraNota(b)));
  return copia;
}

export function encontrarEntregaPorNota(entregas: Entrega[], j10_ukey?: string): Entrega | undefined {
  if (!j10_ukey) return undefined;
  return entregas.find((e) => e.notas.some((n) => n.j10_ukey === j10_ukey));
}
